import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from '@ant-design/icons';

import AgenticRagChatPanel, { useAgenticRagHealth } from './AgenticRagChatPanel';
import './FiIntelligenceChatWidget.css';

const HIDDEN_PATH_PREFIXES = ['/', '/login'];

function shouldShowWidget(pathname) {
  if (pathname === '/') return false;
  if (pathname.startsWith('/login')) return false;
  return true;
}

export default function FiIntelligenceChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [clearSignal, setClearSignal] = useState(0);
  const { status, llmProvider } = useAgenticRagHealth();

  if (!shouldShowWidget(location.pathname)) {
    return null;
  }

  const statusLabel = status === 'online'
    ? `Online · ${llmProvider || 'LLM'}`
    : status === 'checking'
      ? 'Connecting…'
      : 'Offline';

  const handleClose = () => {
    setOpen(false);
    setExpanded(false);
  };

  const handleToggleOpen = () => {
    setOpen((v) => {
      if (v) setExpanded(false);
      return !v;
    });
  };

  return (
    <div
      className={`fi-chat-widget${expanded ? ' fi-chat-widget--expanded' : ''}`}
      aria-live="polite"
    >
      {open && expanded && (
        <div
          className="fi-chat-backdrop"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
      )}
      {open && (
        <div
          className={`fi-chat-popup${expanded ? ' fi-chat-popup--expanded' : ''}`}
          role="dialog"
          aria-label="Financial Intelligence Chat"
        >
          <div className="fi-chat-popup-header">
            <div className="fi-chat-popup-title">
              <span className="fi-chat-popup-logo">📊</span>
              <div>
                <strong>FI Intelligence</strong>
                <span className="fi-chat-popup-sub">Agentic RAG</span>
              </div>
            </div>
            <div className="fi-chat-popup-actions">
              <span className="fi-chat-popup-status">
                <span
                  className={`arag-status-dot ${
                    status === 'online' ? 'online' : status === 'checking' ? 'loading' : 'error'
                  }`}
                />
                {statusLabel}
              </span>
              <button
                type="button"
                className="fi-chat-icon-btn"
                title="Clear conversation"
                onClick={() => setClearSignal((v) => v + 1)}
              >
                <DeleteOutlined />
              </button>
              <button
                type="button"
                className="fi-chat-icon-btn"
                title={expanded ? 'Exit full screen' : 'Full screen'}
                aria-label={expanded ? 'Exit full screen' : 'Full screen'}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              </button>
              <button
                type="button"
                className="fi-chat-icon-btn"
                title="Close"
                onClick={handleClose}
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
          <div className="fi-chat-popup-body">
            <AgenticRagChatPanel compact={!expanded} clearSignal={clearSignal} />
          </div>
        </div>
      )}

      <button
        type="button"
        className={`fi-chat-fab ${open ? 'fi-chat-fab--open' : ''}`}
        onClick={handleToggleOpen}
        aria-label={open ? 'Close financial intelligence chat' : 'Open financial intelligence chat'}
        aria-expanded={open}
      >
        {open ? <CloseOutlined /> : <CommentOutlined />}
      </button>
    </div>
  );
}
